import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check,
  Clock,
  Euro,
  Inbox,
  Loader2,
  LogOut,
  ShieldCheck,
  Ticket,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmée",
  cancelled: "Annulée",
  completed: "Terminée",
};
const STATUS_STYLES = {
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  confirmed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  cancelled: "bg-red-500/15 text-red-400 border-red-500/30",
  completed: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};
const FILTERS = [
  { id: null, label: "Toutes" },
  { id: "pending", label: "En attente" },
  { id: "confirmed", label: "Confirmées" },
  { id: "completed", label: "Terminées" },
  { id: "cancelled", label: "Annulées" },
];

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

const inputCls =
  "w-full border border-line bg-void px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-cyan-400";

export default function AdminPage() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-void">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
      </div>
    );
  }
  if (!user) return <AdminLogin onLogin={setUser} />;
  return <AdminDashboard user={user} onLogout={() => setUser(null)} />;
}

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err, "Connexion impossible"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cyber-grid flex min-h-screen items-center justify-center bg-void px-5">
      <motion.form
        data-testid="admin-login-form"
        onSubmit={submit}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="glass-panel w-full max-w-md p-8"
      >
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-cyan-400" />
          <h1 className="font-display text-xl font-bold uppercase tracking-wide text-slate-100">
            Espace Staff
          </h1>
        </div>
        <p className="mt-2 font-hud text-[11px] uppercase tracking-[0.2em] text-slate-500">
          Immersion Plus — Accès restreint
        </p>
        <div className="mt-8 space-y-4">
          <input
            data-testid="admin-email-input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className={inputCls}
          />
          <input
            data-testid="admin-password-input"
            type="password"
            placeholder="Mot de passe"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className={inputCls}
          />
        </div>
        {error && (
          <p data-testid="admin-login-error" className="mt-4 border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-sm text-red-400">
            {error}
          </p>
        )}
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading}
          className="mt-6 flex w-full items-center justify-center gap-2 bg-cyan-400 py-3.5 font-hud text-xs font-bold uppercase tracking-[0.2em] text-void transition-all duration-300 hover:bg-cyan-300 disabled:opacity-40"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Connexion
        </button>
        <Link
          data-testid="admin-back-home"
          to="/"
          className="mt-5 block text-center font-hud text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-cyan-400"
        >
          ← Retour au site
        </Link>
      </motion.form>
    </div>
  );
}

function AdminDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState(null);
  const [tab, setTab] = useState("reservations");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const [s, b, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings", { params }),
        api.get("/admin/contacts"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setContacts(c.data);
    } catch (e) {
      toast.error(formatApiError(e, "Erreur de chargement"));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
      toast.success(`Réservation ${STATUS_LABELS[status].toLowerCase()}`);
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* session déjà expirée */
    }
    onLogout();
  };

  const KPIS = [
    { icon: Ticket, label: "Réservations", value: stats?.total ?? "—" },
    { icon: Clock, label: "En attente", value: stats?.pending ?? "—" },
    { icon: Check, label: "Confirmées", value: stats?.confirmed ?? "—" },
    { icon: Euro, label: "Revenu estimé", value: stats ? `${stats.revenue}€` : "—" },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-void">
      <header className="border-b border-line bg-surface/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="font-display text-lg font-extrabold tracking-widest text-slate-100">
              IMMERSION<span className="text-cyan-400">+</span>
            </span>
            <span className="hidden border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 font-hud text-[10px] uppercase tracking-[0.2em] text-cyan-300 sm:block">
              Console admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden font-hud text-[11px] text-slate-500 sm:block">
              {user.email}
            </span>
            <Link
              data-testid="admin-view-site"
              to="/"
              className="font-hud text-[11px] uppercase tracking-[0.2em] text-slate-400 transition-colors hover:text-cyan-400"
            >
              Voir le site
            </Link>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 border border-line px-4 py-2 font-hud text-[11px] uppercase tracking-[0.2em] text-slate-300 transition-colors hover:border-red-500/50 hover:text-red-400"
            >
              <LogOut className="h-3.5 w-3.5" /> Sortir
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10">
        <div data-testid="admin-kpis" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {KPIS.map((k) => (
            <div key={k.label} className="border border-line bg-surface p-5">
              <k.icon className="h-5 w-5 text-cyan-400" />
              <div className="mt-3 font-orbitron text-3xl font-black text-slate-100">
                {k.value}
              </div>
              <div className="mt-1 font-hud text-[10px] uppercase tracking-[0.25em] text-slate-500">
                {k.label}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2">
            <button
              data-testid="admin-tab-reservations"
              onClick={() => setTab("reservations")}
              className={`border px-4 py-2 font-hud text-xs uppercase tracking-[0.2em] transition-all ${
                tab === "reservations"
                  ? "border-cyan-400 bg-cyan-400/10 text-cyan-300"
                  : "border-line text-slate-400 hover:text-slate-200"
              }`}
            >
              Réservations
            </button>
            <button
              data-testid="admin-tab-messages"
              onClick={() => setTab("messages")}
              className={`flex items-center gap-2 border px-4 py-2 font-hud text-xs uppercase tracking-[0.2em] transition-all ${
                tab === "messages"
                  ? "border-cyan-400 bg-cyan-400/10 text-cyan-300"
                  : "border-line text-slate-400 hover:text-slate-200"
              }`}
            >
              <Inbox className="h-3.5 w-3.5" /> Messages ({contacts.length})
            </button>
          </div>
          {tab === "reservations" && (
            <div data-testid="admin-filters" className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f.label}
                  data-testid={`admin-filter-${f.id || "all"}`}
                  onClick={() => setFilter(f.id)}
                  className={`px-3 py-1.5 font-hud text-[10px] uppercase tracking-[0.2em] transition-all ${
                    filter === f.id
                      ? "bg-cyan-400 text-void"
                      : "border border-line text-slate-400 hover:text-slate-200"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
          </div>
        ) : tab === "reservations" ? (
          <div data-testid="admin-bookings-table" className="mt-6 overflow-x-auto border border-line">
            {bookings.length === 0 ? (
              <p className="p-10 text-center text-sm text-slate-500">
                Aucune réservation pour ce filtre.
              </p>
            ) : (
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead>
                  <tr className="border-b border-line bg-surface font-hud text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    <th className="px-4 py-3">Réf</th>
                    <th className="px-4 py-3">Client</th>
                    <th className="px-4 py-3">Expérience</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Machine</th>
                    <th className="px-4 py-3">Total</th>
                    <th className="px-4 py-3">Statut</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr
                      key={b.id}
                      data-testid={`booking-row-${b.reference}`}
                      className="border-b border-line/60 bg-void transition-colors hover:bg-surface/60"
                    >
                      <td className="px-4 py-3 font-hud text-xs text-cyan-300">{b.reference}</td>
                      <td className="px-4 py-3">
                        <div className="text-slate-200">{b.name}</div>
                        <div className="text-xs text-slate-500">{b.phone}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {b.experience}
                        <span className="block text-xs text-slate-500">
                          {b.players} joueur(s) · {b.duration} min
                        </span>
                      </td>
                      <td className="px-4 py-3 font-hud text-xs text-slate-300">
                        {formatDate(b.date)}
                        <span className="block text-cyan-400">{b.time_slot}</span>
                      </td>
                      <td className="px-4 py-3 font-hud text-xs uppercase text-slate-400">
                        {b.machine === "katvr" ? "KAT VR" : "Chaise 360°"}
                      </td>
                      <td className="px-4 py-3 font-orbitron text-sm font-bold text-slate-200">
                        {b.total_price}€
                      </td>
                      <td className="px-4 py-3">
                        <span
                          data-testid={`booking-status-${b.reference}`}
                          className={`inline-block border px-2.5 py-1 font-hud text-[10px] uppercase tracking-wider ${STATUS_STYLES[b.status]}`}
                        >
                          {STATUS_LABELS[b.status]}
                        </span>
                        {b.payment_status === "paid" ? (
                          <span
                            data-testid={`booking-paid-${b.reference}`}
                            className="ml-1.5 inline-block border border-lime-500/40 bg-lime-500/10 px-2 py-1 font-hud text-[10px] uppercase tracking-wider text-lime-400"
                          >
                            Payée
                          </span>
                        ) : (
                          <span className="ml-1.5 inline-block border border-line px-2 py-1 font-hud text-[10px] uppercase tracking-wider text-slate-500">
                            Sur place
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {b.status === "pending" && (
                            <button
                              data-testid={`booking-confirm-${b.reference}`}
                              onClick={() => updateStatus(b.id, "confirmed")}
                              className="border border-emerald-500/40 p-1.5 text-emerald-400 transition-colors hover:bg-emerald-500/15"
                              title="Confirmer"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          )}
                          {b.status === "confirmed" && (
                            <button
                              data-testid={`booking-complete-${b.reference}`}
                              onClick={() => updateStatus(b.id, "completed")}
                              className="border border-cyan-500/40 px-2 py-1 font-hud text-[10px] uppercase text-cyan-300 transition-colors hover:bg-cyan-500/15"
                            >
                              Terminer
                            </button>
                          )}
                          {(b.status === "pending" || b.status === "confirmed") && (
                            <button
                              data-testid={`booking-cancel-${b.reference}`}
                              onClick={() => updateStatus(b.id, "cancelled")}
                              className="border border-red-500/40 p-1.5 text-red-400 transition-colors hover:bg-red-500/15"
                              title="Annuler"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <div data-testid="admin-contacts-list" className="mt-6 space-y-3">
            {contacts.length === 0 ? (
              <p className="border border-line p-10 text-center text-sm text-slate-500">
                Aucun message reçu.
              </p>
            ) : (
              contacts.map((c) => (
                <div key={c.id} className="border border-line bg-surface p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-200">{c.name}</span>
                    <span className="font-hud text-xs text-cyan-300">{c.email}</span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{c.message}</p>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}
